import moment from 'moment';
import { useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import createCachedSelector from 're-reselect';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '../../constants';
import { formatMainProjectLabel, getMainProjectDateLabel } from '../../utils/scheduling';
import * as api from '../../services/api';
import { generateRange } from '../../utils/time';
import { selectedBranch } from '../../atoms/shared';
import useCRUD from '../useCRUD';
import useAccount from './useAccount';

export const CREATE_NEW_MAIN_PROJECT = '__CREATE_NEW__';

const mainProjectsSelector = createCachedSelector(
  (response) => response?.data || [],
  (mainProjects) => {
    const mainProjectById = mainProjects.reduce((map, mp) => {
      map[mp.id] = mp;
      return map;
    }, {});

    const mainProjectsByDate = mainProjects.reduce((all, mainProject) => {
      const datesInRange =
        generateRange({
          start: mainProject.startDate,
          end: mainProject.endDate || mainProject.startDate
        }).map((dateAsMoment) => dateAsMoment.format(API_DATE_FORMAT)) || [];

      datesInRange
        .filter((currDate) => !!currDate)
        .forEach((currDate) => {
          if (!all[currDate]) {
            all[currDate] = [];
          }
          all[currDate].push(mainProject);
        });
      return all;
    }, {});

    return { mainProjects, mainProjectById, mainProjectsByDate };
  }
)({
  keySelector: (response, storeKey) => `${storeKey.join('#')}`
});

const allMainProjectsSelector = createCachedSelector(
  (response) => response?.data || [],
  (allMainProjects) => ({ allMainProjects })
)({
  keySelector: (response, storeKey) => `${storeKey.join('#')}`
});

const useMainProjects = ({ timeRange, enabled = true, showAllBranches = true }) => {
  const { t, shortDateFormat } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const branchId = useRecoilValue(selectedBranch);
  const queryClient = useQueryClient();

  const from = useMemo(() => (timeRange?.startDate ? moment(timeRange.startDate).format(API_DATE_FORMAT) : undefined), [
    timeRange?.startDate
  ]);
  const to = useMemo(() => (timeRange?.endDate ? moment(timeRange.endDate).format(API_DATE_FORMAT) : undefined), [
    timeRange?.endDate
  ]);

  const storeKey = useMemo(() => ['main-projects', accountId, branchId, from, to, showAllBranches], [
    accountId,
    branchId,
    from,
    to,
    showAllBranches
  ]);

  const { isLoading, data, refetch } = useQuery(
    storeKey,
    () => api.fetchMainProjects({ accountId, branchId, from, to, showAllBranches }),
    {
      enabled: enabled && !!accountId && !!branchId && !!from && !!to,
      staleTime: 0,
      select: (response) => mainProjectsSelector(response, storeKey)
    }
  );

  const { mainProjects = [], mainProjectById = {}, mainProjectsByDate = {} } = data || {};

  // Separate unfiltered query for dropdown options (all main projects regardless of date range)
  const allMainProjectsStoreKey = useMemo(() => ['main-projects-all', accountId, branchId], [accountId, branchId]);

  const { data: allMainProjectsData } = useQuery(
    allMainProjectsStoreKey,
    () => api.fetchMainProjects({ accountId, branchId }),
    {
      enabled: enabled && !!accountId && !!branchId,
      staleTime: 30000,
      select: (response) => allMainProjectsSelector(response, allMainProjectsStoreKey)
    }
  );

  const { allMainProjects = [] } = allMainProjectsData || {};

  const allMainProjectById = useMemo(
    () =>
      allMainProjects.reduce((map, mp) => {
        map[mp.id] = mp;
        return map;
      }, {}),
    [allMainProjects]
  );

  const mainProjectOptions = useMemo(
    () => [
      ...allMainProjects.map((mp) => ({
        value: mp.id,
        label: formatMainProjectLabel(mp, shortDateFormat),
        name: mp.name,
        dateLabel: getMainProjectDateLabel(mp, `ddd, ${shortDateFormat}`),
        startDate: mp.startDate,
        endDate: mp.endDate,
        color: mp.color
      })),
      { value: CREATE_NEW_MAIN_PROJECT, label: t('addMainEvent') }
    ],
    [allMainProjects, shortDateFormat, t]
  );

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: (mainProject) => api.createMainProject({ accountId, branchId, ...mainProject }),
      editApi: ({ id, ...fields }) => api.updateMainProject({ accountId, branchId, mainProjectId: id, ...fields }),
      deleteApi: ({ id }) => api.deleteMainProject({ accountId, branchId, mainProjectId: id })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        queryClient.invalidateQueries(allMainProjectsStoreKey);
        queryClient.invalidateQueries('projects');
      }
    }
  );

  const { mutateAsync: createMainProjectAcrossBranches, isLoading: isCreatingMainProjectAcrossBranches } = useMutation(
    (payload) => api.createMainProjectAcrossBranches({ accountId, ...payload }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['main-projects']);
        queryClient.invalidateQueries(['main-projects-all']);
        queryClient.invalidateQueries('projects');
      }
    }
  );

  const { mutateAsync: linkProjectsToMainEvent } = useMutation(
    ({ mainProjectId, projectIds }) => api.linkProjectsToMainEvent({ accountId, branchId, mainProjectId, projectIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        queryClient.invalidateQueries(allMainProjectsStoreKey);
        queryClient.invalidateQueries('projects');
      }
    }
  );

  return {
    isLoading,
    mainProjects,
    mainProjectById,
    allMainProjectById,
    mainProjectOptions,
    mainProjectsByDate,
    addMainProject: addItem,
    editMainProject: editItem,
    deleteMainProject: deleteItem,
    createMainProjectAcrossBranches,
    isCreatingMainProjectAcrossBranches,
    linkProjectsToMainEvent,
    refetch
  };
};

export default useMainProjects;
